<template>
  <div class="min-h-screen w-full">
    <!-- Header -->
    <header class="my-10 text-center pb-10">
      <h1 class="text-4xl md:text-5xl align-middle font-semibold text-gray-900">
        Features Store
      </h1>
      <p class="text-lg md:text-xl text-gray-500">Filter by features</p>
    </header>

    <!-- Main Layout -->
    <div class="w-full px-4 py-6">
      <div class="flex flex-col md:flex-row gap-4">
        <!-- Sidebar with Features -->
        <aside
          class="md:w-72 w-full flex-shrink-0 bg-gray-100 rounded-lg shadow-sm border-gray-200 p-4 md:p-5 md:mr-6"
        >
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-base md:text-lg font-semibold text-[#157813]">
              Filters
            </h2>
            <button
              @click="clearAllFilters"
              class="text-sm text-[#157813] hover:text-[#46cc44] flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="hidden md:inline">Clear All</span>
            </button>
          </div>

          <!-- Features Section -->
          <div class="mb-6 md:mb-10">
            <div class="flex justify-between items-center mb-2">
              <h2 class="text-base md:text-lg font-semibold text-[#157813]">
                Features
              </h2>
              <button
                @click="clearFeatures"
                class="text-sm text-[#157813] hover:text-[#46cc44] flex items-center gap-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
                Clear
              </button>
            </div>
            <input
              v-model="featureSearch"
              type="text"
              placeholder="Search features..."
              class="w-full mb-2 px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-[#157813] text-xs md:text-sm"
            />
            
            <!-- Debug info for pagination -->
            <div class="text-xs text-gray-400 px-2 py-1 border-b mb-2">
              Total: {{ uniqueFeatures.length }} features ({{ filteredFeatures.length }} loaded)
            </div>
            
            <div
              class="h-[28rem] md:h-[40rem] overflow-y-auto overflow-x-auto border border-gray-200 rounded p-2"
            >
              <label
                v-for="feature in filteredFeatures"
                :key="feature"
                class="block text-xs md:text-sm mb-2 md:mb-3 whitespace-nowrap"
              >
                <input
                  type="checkbox"
                  :value="feature"
                  :checked="selectedFeatures.includes(feature)"
                  @change="toggleFeature(feature)"
                  class="mr-1"
                  style="accent-color: #157813"
                />
                {{ feature }}
              </label>
              
              <!-- Loading indicator for infinite scroll -->
              <div
                v-if="isFetchingMoreFeatures"
                class="flex items-center justify-center py-3 text-sm bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 mx-1"
              >
                <div class="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#157813] mr-3"></div>
                <span class="text-gray-700 font-medium">Loading more features...</span>
              </div>
              
              <!-- Load more button (fallback) -->
              <button
                v-else-if="hasMoreFeatures && filteredFeatures.length > 0"
                @click="handleLoadMoreFeatures"
                class="w-full py-2 text-xs text-[#157813] hover:text-[#46cc44] border border-gray-200 rounded mt-2"
              >
                Load More Features
              </button>
              
              <p
                v-if="filteredFeatures.length === 0 && !isFetchingMoreFeatures"
                class="text-gray-400 text-xs md:text-sm"
              >
                No matching features
              </p>
            </div>
          </div>


        </aside>

        <!-- Table of Queries -->
        <main class="flex-1 min-w-0">
          <div class="text-sm text-gray-600 mb-4 px-1">
            Showing {{ displayedQueries.length }} of {{ filteredQueries.length }} results
          </div>
            <div class="overflow-x-auto">
              <QueryTable
                :queries="displayedQueries"
                :headers="tableHeaders"
                :sort-state="sortState"
                @sort="sortBy"
                @row-click="goToDetails"
                @toggle-unique="handleToggleUnique"
            />
          </div>
        </main>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredQueries.length > itemsPerPage" class="mt-4">
      <Pagination
        :current-page="currentPage"
        :total-items="filteredQueries.length"
        :items-per-page="itemsPerPage"
        @page-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import Pagination from "@/components/Pagination.vue";
import QueryTable from "@/components/QueryTable.vue";
import SuggestionDropdown from "@/components/SuggestionDropdown.vue";
import { Query, transformQueries } from "@/models/Query";
import rawQueries from "@/queries.json";
import { clearFiltersFromStorage, loadFiltersFromStorage, saveFiltersToStorage, type FilterState } from "@/utils/filterPersistence";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

export default defineComponent({
  name: "FeaturesStore",
  components: {
    QueryTable,
    LoadingIndicator,
    Pagination,
    SuggestionDropdown
  },
  setup() {
const router = useRouter();
    
    // Load and transform queries
    const queries = ref<Query[]>(transformQueries(rawQueries as any[]));
    const currentPage = ref(1);
    const itemsPerPage = 20;
    const displayedQueries = ref<Query[]>([]);

    // Filter state
    const selectedFeatures = ref<string[]>([]);
const featureSearch = ref("");
    const searchQuery = ref("");
    const showUniqueQueries = ref(true);
    const selectedAuthors = ref<string[]>([]);

// Sorting state
const sortState = ref<{
  key: string;
  direction: "asc" | "desc";
}>({
  key: "id",
  direction: "asc",
});

    // Unique features
const uniqueFeatures = computed(() => {
  const featureSet = new Set<string>();

  // Extract unique features from all queries
  queries.value.forEach((q) => q.features.forEach((f) => featureSet.add(f)));

  return Array.from(featureSet).sort();
});



    // Filtered features
const filteredFeatures = computed(() => {
  const search = featureSearch.value.toLowerCase();
      return uniqueFeatures.value.filter((f) =>
    f.toLowerCase().includes(search)
  );
});



    // Filtered and sorted queries
    const filteredQueries = computed(() => {
      let filtered = queries.value;

      // Apply feature filters
      if (selectedFeatures.value.length > 0) {
        filtered = filtered.filter((q) => {
          const hasAllFeatures = selectedFeatures.value.every((f) => q.features.includes(f));
          return hasAllFeatures;
        });
      }

      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter((q) =>
          q.neuronText.toLowerCase().includes(query)
        );
      }

      // Apply author filter
      if (selectedAuthors.value.length > 0) {
        filtered = filtered.filter((q) =>
          selectedAuthors.value.includes(q.author)
        );
      }

      // Apply unique filter if enabled
      if (showUniqueQueries.value) {
        const uniqueMap = new Map();
        filtered.forEach((query) => {
          if (!uniqueMap.has(query.neuronId)) {
            uniqueMap.set(query.neuronId, query);
          }
        });
        filtered = Array.from(uniqueMap.values());
      }

      // Apply sorting
    if (sortState.value.key) {
        filtered.sort((a, b) => {
        const aValue = a[sortState.value.key as keyof Query];
        const bValue = b[sortState.value.key as keyof Query];

          if (sortState.value.key === "id") {
            // Sort by ID as string, but handle numeric parts properly
            const aStr = String(aValue || "");
            const bStr = String(bValue || "");
            return sortState.value.direction === "asc"
              ? aStr.localeCompare(bStr, undefined, { numeric: true })
              : bStr.localeCompare(aStr, undefined, { numeric: true });
          }

        if (sortState.value.key === "neuronId") {
            // Extract numbers after AST_ for comparison
            const numA = parseInt((String(aValue).split('AST_')[1] || '0'), 10);
            const numB = parseInt((String(bValue).split('AST_')[1] || '0'), 10);
          return sortState.value.direction === "asc" ? numA - numB : numB - numA;
        }

        if (sortState.value.key === "queryStartTime") {
          return sortState.value.direction === "asc"
            ? new Date(a.queryStartTime).getTime() - new Date(b.queryStartTime).getTime()
            : new Date(b.queryStartTime).getTime() - new Date(a.queryStartTime).getTime();
        }

        if (sortState.value.key === "extraction") {
            const aExtractionCount = Array.isArray(a.extraction) ? a.extraction.length : 0;
            const bExtractionCount = Array.isArray(b.extraction) ? b.extraction.length : 0;
          return sortState.value.direction === "asc"
            ? aExtractionCount - bExtractionCount
            : bExtractionCount - aExtractionCount;
        }

        if (typeof aValue === "string") {
          return sortState.value.direction === "asc"
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        }

        return sortState.value.direction === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      });
    }
    
      return filtered;
    });

    // Sorting handler
    const sortBy = (key: string) => {
      if (sortState.value.key === key) {
        sortState.value.direction =
          sortState.value.direction === "asc" ? "desc" : "asc";
      } else {
        sortState.value.key = key;
        sortState.value.direction = "asc";
      }
    };

    // Clear filters
    const clearFeatures = () => {
      selectedFeatures.value = [];
      featureSearch.value = "";
    };

    // Toggle feature selection
    const toggleFeature = (feature: string) => {
      const index = selectedFeatures.value.indexOf(feature);
      if (index === -1) {
        selectedFeatures.value.push(feature);
      } else {
        selectedFeatures.value.splice(index, 1);
      }
    };

    // Infinite scroll state (mock implementation for now)
    const isFetchingMoreFeatures = ref(false);
    const hasMoreFeatures = ref(false);

    // Load more features handler
    const handleLoadMoreFeatures = () => {
      // This would typically load more features from an API
      console.log('Loading more features...');
    };

    const clearAllFilters = () => {
      clearFeatures();
      selectedAuthors.value = [];
      searchQuery.value = "";
      showUniqueQueries.value = true;
      
      // Clear from localStorage as well
      clearFiltersFromStorage();
    };

    // Filter persistence functions
    const saveCurrentFilters = () => {
      const filterState: FilterState = {
        searchTerm: searchQuery.value,
        selectedFeatures: selectedFeatures.value,
        selectedTags: [], // Not used in this view
        selectedAuthors: selectedAuthors.value,
        showUniqueQueries: showUniqueQueries.value,
      };
      saveFiltersToStorage(filterState);
    };

    const loadPersistedFilters = () => {
      const persisted = loadFiltersFromStorage();
      if (persisted) {
        if (persisted.searchTerm) searchQuery.value = persisted.searchTerm;
        if (persisted.selectedFeatures) selectedFeatures.value = persisted.selectedFeatures;
        if (persisted.selectedAuthors) selectedAuthors.value = persisted.selectedAuthors;
        if (persisted.showUniqueQueries !== undefined) showUniqueQueries.value = persisted.showUniqueQueries;
      }
    };

    // Navigation
    const goToDetails = (queryId: string) => {
      // Navigate to query details in the same tab
      router.push(`/query/${queryId}`);
    };

    // Handle page change
    const handlePageChange = (page: number) => {
      currentPage.value = page;
      updateDisplayedQueries();
    };

    // Update displayed queries based on current page
    const updateDisplayedQueries = () => {
      const startIndex = (currentPage.value - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      displayedQueries.value = filteredQueries.value.slice(startIndex, endIndex);
    };

    // Add watch for filteredQueries with loading state
    watch(filteredQueries, (newValue) => {
      currentPage.value = 1;
      updateDisplayedQueries();
    }, { immediate: true });

    // Watch for filter changes and save to localStorage
    watch([searchQuery, selectedFeatures, selectedAuthors, showUniqueQueries], () => {
      saveCurrentFilters();
    }, { deep: true });

const tableHeaders = [
  { key: "id", label: "Query ID" },
  { key: "neuronId", label: "Neuron ID" },
  { key: "neuronText", label: "SQL Query", searchable: true },
  { key: "author", label: "Author" },
  { key: "extraction", label: "Run Count" },
  { key: "queryStartTime", label: "Run Date" },
];

    const handleToggleUnique = (show: boolean) => {
      showUniqueQueries.value = show;
    };

    // Load persisted filters on mount
    onMounted(() => {
      loadPersistedFilters();
    });

    return {
      tableHeaders,
      queries,
      displayedQueries,
      selectedFeatures,
      featureSearch,
      filteredFeatures,
      filteredQueries,
      uniqueFeatures,
      sortState,
      sortBy,
      clearFeatures,
      clearAllFilters,
      toggleFeature,
      goToDetails,
      handlePageChange,
      updateDisplayedQueries,
      showUniqueQueries,
      handleToggleUnique,
      selectedAuthors,
      isFetchingMoreFeatures,
      hasMoreFeatures,
      handleLoadMoreFeatures,
      currentPage,
      itemsPerPage,
    };
  },
});
</script>

<style scoped>
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 5px;
}
::-webkit-scrollbar-thumb {
  background: #157813;
  border-radius: 5px;
}
::-webkit-scrollbar-thumb:hover {
  background: #46cc44;
}

@media (max-width: 768px) {
  table {
    min-width: 600px;
  }
}
</style>
